import React, { useState, useEffect, useRef } from 'react'
import { Modal, Spinner } from 'reactstrap'
import { FaTimes, FaEdit, FaTrash, FaCheck } from 'react-icons/fa'
import { useToasts } from 'react-toast-notifications'
import { useDispatch } from 'react-redux'
import useCategories from '../hooks/useCategories'
import useCategorySort from '../hooks/useCategorySort'
import useCategoryEditDelete from '../hooks/useCategoryEditDelete'
import categoryApi from '../../../api/category'
import { getCategoryListAction } from '../../../redux/modules/category'

export default function CategoryModal({ isOpen, toggle }) {
  const { categories } = useCategories()
  const sortedCategories = useCategorySort(categories)
  const {
    editingId,
    editingName,
    setEditingName,
    isSavingEdit,
    isDeletingId,
    handleEditClick,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteCategory,
    resetEditState,
  } = useCategoryEditDelete()

  const { addToast } = useToasts()
  const dispatch = useDispatch()
  const listRef = useRef(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [items, setItems] = useState([])
  const [newCategoryName, setNewCategoryName] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [newlyAddedId, setNewlyAddedId] = useState(null)

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setNewCategoryName('')
      setNewlyAddedId(null)
      resetEditState()
    }
  }, [isOpen, resetEditState])

  useEffect(() => {
    if (sortedCategories) {
      if (searchQuery.trim() === '') {
        setItems(sortedCategories)
      } else {
        setItems(
          sortedCategories.filter((cat) =>
            cat.name.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      }
    }
  }, [sortedCategories, searchQuery])

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return
    const nameToAdd = newCategoryName.trim()
    try {
      setIsAdding(true)
      const data = { name: nameToAdd }
      const response = await categoryApi.addCategory(data)
      addToast(response?.msgResp?.message || 'Category added successfully', { appearance: 'success', autoDismiss: true })
      setNewCategoryName('')

      const addedId = response?.data?.id || response?.msgResp?.id || response?.id
      await dispatch(getCategoryListAction(true))

      setNewlyAddedId(addedId || nameToAdd)

      if (listRef.current) {
        listRef.current.scrollTop = 0
      }

      setTimeout(() => {
        setNewlyAddedId(null)
      }, 2500)
    } catch (error) {
      addToast(error?.msgResp || 'Failed to add category', { appearance: 'error', autoDismiss: true })
    } finally {
      setIsAdding(false)
    }
  }

  const handleClose = () => {
    if (toggle) toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} className='modal-from-product' centered>
      <div className='modal-from-product__container'>
        <div className='modal-from-product__header'>
          <h3 className='modal-from-product__title'>Category List</h3>
          <button type='button' className='modal-from-product__close-btn' onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        <div className='modal-from-product__search-wrapper'>
          <i className='modal-from-product__search-icon icon-search'></i>
          <input
            type='text'
            className='modal-from-product__search-input'
            placeholder='Search categories...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        {/* List of Categories */}
        <div id='scrollableDivCategory' ref={listRef} className='modal-from-product__list'>
          {items.length === 0 ? (
            <div className='modal-from-product__empty'>No categories found</div>
          ) : ( // eslint-disable-next-line arrow-body-style
            items.map((category, index) => {
              const isFlashing =
                newlyAddedId &&
                (category.id === newlyAddedId ||
                  category.name === newlyAddedId ||
                  (index === 0 && (newlyAddedId === category.name || newlyAddedId === category.id)))

              return (
                <div
                  key={category.id}
                  className={`modal-from-product__item ${isFlashing ? 'flash-border' : ''}`}
                >
                  <div className='modal-from-product__top-row'>
                    {editingId === category.id ? (
                      <div className='edit-input-wrapper' style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <input
                          type='text'
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className='form-control'
                          style={{ fontSize: '14px', height: '34px' }}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit(category.id)
                            if (e.key === 'Escape') handleCancelEdit()
                          }}
                        />
                        {isSavingEdit ? (
                          <Spinner size='sm' color='primary' />
                        ) : (
                          <>
                            <FaCheck
                              style={{ color: '#51aaaa', cursor: 'pointer', fontSize: '15px', flexShrink: 0 }}
                              title='Save'
                              onClick={() => handleSaveEdit(category.id)}
                            />
                            <FaTimes
                              style={{ color: '#7D8B9A', cursor: 'pointer', fontSize: '15px', flexShrink: 0 }}
                              title='Cancel'
                              onClick={handleCancelEdit}
                            />
                          </>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className='modal-from-product__info'>
                          <h4 className='modal-from-product__item-name'>{category.name}</h4>
                          <p className='modal-from-product__item-desc'>Type: {category.type}</p>
                        </div>
                        <div className='menu-actions' style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: 'auto', paddingLeft: '12px' }}>
                          {isDeletingId === category.id ? (
                            <Spinner size='sm' color='danger' />
                          ) : (
                            <>
                              <FaEdit
                                style={{ color: '#7D8B9A', cursor: 'pointer', fontSize: '14px' }}
                                title='Edit'
                                onClick={() => handleEditClick(category)}
                              />
                              <FaTrash
                                style={{ color: '#7D8B9A', cursor: 'pointer', fontSize: '14px' }}
                                title='Delete'
                                onClick={() => handleDeleteCategory(category.id)}
                              />
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className='modal-from-product__footer' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <input
            type="text"
            className="form-control"
            style={{ width: '250px', fontSize: '13px' }}
            placeholder="New category name..."
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
          <div className='modal-from-product__actions'>
            <button
              type='button'
              className='modal-from-product__btn-cancel'
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type='button'
              className='modal-from-product__btn-submit'
              disabled={!newCategoryName.trim() || isAdding}
              onClick={handleAddCategory}
            >
              {isAdding ? <Spinner size='sm' /> : 'Add Category'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
